import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import styles from './styles.module.scss'
import { useEffect, useRef, useState } from 'react'

export const BarChart = ({ reviewsRatingData, selectedCategory }) => {
  const chartRef = useRef<HTMLCanvasElement | null>(null)
  const [windowWidth, setWindowWidth] = useState<number | undefined>(undefined)

  useEffect(() => {
    const handleWindowResize = () => {
      if (typeof window !== 'undefined') {
        const newWidth = window.innerWidth
        if (newWidth !== windowWidth) {
          setWindowWidth(newWidth)
        }
      }
    }

    if (typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth)
      window.addEventListener('resize', handleWindowResize)
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleWindowResize)
      }
    }
  }, [windowWidth])

  useEffect(() => {
    if (chartRef.current && typeof window !== 'undefined') {
      const isMobile = windowWidth && windowWidth <= 860
      const ctx = chartRef.current.getContext('2d')

      if (ctx) {
        Chart.getChart(chartRef.current)?.destroy()

        const scores = reviewsRatingData.map(review => review.score)
        const reviewNames = reviewsRatingData.map(review => review.reviewName)
        const categories = reviewsRatingData.map(review => review.category)

        const gradientNormal = ctx.createLinearGradient(0, 0, 0, 300)
        gradientNormal.addColorStop(0, '#3D8A3D')
        gradientNormal.addColorStop(1, '#192E1A')

        const gradientHighlight = ctx.createLinearGradient(0, 0, 0, 300)
        gradientHighlight.addColorStop(0, '#4CAF50')
        gradientHighlight.addColorStop(1, '#214E2B')

        const minRating = Math.min(...scores)
        const maxRating = Math.max(...scores)

        let minTick = Math.floor(minRating) - 1

        if (minTick > 8.6) {
          minTick = 8.6
        }

        const maxTick = 10 // Changed to 10 so if all ratings for example are below 9 it will still have 10 at the max tick.
        const isManyReviews = reviewsRatingData.length > 7 // Used to set the chart to

        Chart.register(ChartDataLabels)

        new Chart(ctx, {
          type: 'bar',
          data: {
            labels: reviewNames,
            datasets: [
              {
                label: selectedCategory,
                data: scores,
                backgroundColor: categories.map(category =>
                  category === selectedCategory
                    ? gradientHighlight
                    : gradientNormal
                ),
                categoryPercentage: isMobile || isManyReviews ? 0.88 : 0.98,
                barPercentage: isMobile || isManyReviews ? 0.88 : 0.98,
              },
            ],
          },
          options: {
            maintainAspectRatio: false, // Change to control the height and width
            indexAxis: isMobile || isManyReviews ? 'y' : 'x',
            scales: {
              x: {
                display: true, // Showing the
                min: isMobile || isManyReviews ? minTick : undefined,
                max: isMobile || isManyReviews ? maxTick : undefined,
                grid: {
                  display: isMobile || isManyReviews,
                  color: '#3B3B3B',
                },
                ticks: {
                  stepSize:
                    isMobile || isManyReviews ? (maxTick - minTick) / 3 : 1, // Makes sure that the ticks in x axis always show 5 lines in mobile
                  precision: 1, // Ticks under chart when the indexAxis is Y
                  color: 'rgba(255, 255, 255, 0.90)',
                  font: {
                    size: 14,
                  },
                },
              },
              y: {
                position: 'right',
                beginAtZero: false,
                min: !isMobile && !isManyReviews ? minTick : undefined,
                max: !isMobile && !isManyReviews ? maxTick : undefined,
                grid: {
                  display: !isMobile && !isManyReviews,
                  color: '#3B3B3B',
                },
                ticks: {
                  z: isMobile ? 5 : undefined,
                  color: 'rgba(255, 255, 255, 0.90)',
                  stepSize: isMobile ? undefined : (maxTick - minTick) / 3,
                  precision: 1,
                  font: {
                    size: 14,
                  },
                },
              },
            },
            plugins: {
              legend: {
                display: false,
                labels: {
                  color: 'white',
                },
              },
              datalabels: {
                display: true,
                color: context => {
                  const dataIndex = context.dataIndex
                  const rating = reviewsRatingData[dataIndex].score
                  return rating === maxRating ? 'white' : 'white' // If we want to change the max rating to another color
                },
                align: isManyReviews || isMobile ? 'start' : 'end',
                anchor: 'end',
                formatter: value => value.toFixed(1),
                labels: {
                  title: {
                    font: {
                      weight: 'bold',
                      size: 16, // Font size for score
                    },
                  },
                },
              },
            },
            elements: {
              line: {
                borderColor: '#3B3B3B',
              },
            },
          },
        })
      }
    }
  }, [selectedCategory, reviewsRatingData, windowWidth])

  return (
    <div className={styles.chartContainer}>
      <canvas className={styles.chartCanvas} ref={chartRef}></canvas>
    </div>
  )
}
